import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Loader2, Shield, Clock, CheckCircle, XCircle, ArrowLeft, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * iter 99 — Supervisor / Admin inbox for pending recovery-key
 * regeneration requests. Reached via the bell-notification deep
 * link or via the Admin menu.
 */
const RecoveryKeyApprovalsPage = () => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const focusedId = params.get('requestId');

  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [acting, setActing] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const role = (user?.role || '').toLowerCase();
  const isAuthorized = ['admin', 'supervisor', 'state_supervisor'].includes(role);

  const fetchPending = async () => {
    try {
      const res = await api.get('/admin/recovery-key-approvals/pending');
      setRequests(res.data?.requests || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load pending requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthorized) return;
    fetchPending();
    const i = setInterval(fetchPending, 15000); // soft live-refresh
    return () => clearInterval(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthorized]);

  const minsLeft = (expires_at) => {
    if (!expires_at) return '—';
    const ms = new Date(expires_at).getTime() - Date.now();
    if (ms <= 0) return 'Expired';
    const mins = Math.floor(ms / 60000);
    const secs = Math.floor((ms % 60000) / 1000);
    return `${mins}m ${secs.toString().padStart(2, '0')}s`;
  };

  const handleApprove = async (req) => {
    if (acting) return;
    setActing(true);
    try {
      await api.post(`/admin/recovery-key-approvals/${req.id}/approve`);
      toast.success('Approved. The user will be notified to view their new key.');
      await fetchPending();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not approve');
    } finally {
      setActing(false);
    }
  };

  const handleBlock = async (req) => {
    if (acting) return;
    if (!window.confirm(`Restrict & force-logout ${req.user_full_name}? This cancels their rotation, leaves their existing recovery key unchanged, and immediately logs them out of the app.`)) return;
    setActing(true);
    try {
      await api.post(`/admin/recovery-key-approvals/${req.id}/block`);
      toast.success(`${req.user_full_name} has been logged out and their rotation cancelled.`);
      await fetchPending();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not block');
    } finally {
      setActing(false);
    }
  };

  const submitReject = async () => {
    if (!rejectTarget || acting) return;
    setActing(true);
    try {
      await api.post(`/admin/recovery-key-approvals/${rejectTarget.id}/reject`, {
        reason: rejectReason.trim() || null,
      });
      toast.success('Request declined. The user has been notified.');
      setRejectTarget(null);
      setRejectReason('');
      await fetchPending();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not reject');
    } finally {
      setActing(false);
    }
  };

  if (!isAuthorized) {
    return (
      <div className="min-h-screen p-4 flex items-center justify-center">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
            <h2 className="font-semibold mb-1">Not authorized</h2>
            <p className="text-sm text-muted-foreground">Only supervisors and admins can view this page.</p>
            <Button className="mt-4" onClick={() => navigate('/')}>Go Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 pb-24" data-testid="recovery-key-approvals-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-5">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Shield className="w-6 h-6 text-[#586D52]" />
              Recovery Key Approvals
            </h1>
            <p className="text-sm text-muted-foreground">Approve or decline a user's request to rotate their recovery key.</p>
          </div>
        </div>

        {loading ? (
          <Card><CardContent className="p-8 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin" /></CardContent></Card>
        ) : requests.length === 0 ? (
          <Card data-testid="recovery-key-approvals-empty">
            <CardContent className="p-8 text-center">
              <Shield className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No pending requests right now.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {requests.map((r) => (
              <Card
                key={r.id}
                data-testid={`recovery-key-approval-${r.id}`}
                className={focusedId === r.id ? 'ring-2 ring-[#586D52]' : ''}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <CardTitle className="text-base truncate">{r.user_full_name}</CardTitle>
                      <p className="text-xs text-muted-foreground truncate">{r.user_email} · {r.user_state || '—'}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full" data-testid={`recovery-key-approval-${r.id}-countdown`}>
                        <Clock className="w-3 h-3" />
                        {minsLeft(r.expires_at)}
                      </span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 space-y-3">
                  <div className="text-xs text-muted-foreground">
                    Requested at {new Date(r.created_at).toLocaleString()}.
                  </div>
                  {r.flow === 'admin_self_rotation' ? (
                    <>
                      <div className="rounded-md bg-red-50 dark:bg-red-900/15 border border-red-200 dark:border-red-800 p-3 text-xs text-red-900 dark:text-red-200">
                        <p className="font-semibold mb-1">⚠ Admin alteration — peer review</p>
                        <p className="mb-1">
                          Another admin started their own recovery-key rotation. If unauthorized,
                          click <strong>Restrict & Force Logout</strong> NOW to cancel the rotation and
                          eject them from the app.
                        </p>
                        <p>
                          Auto-approves at {r.auto_approve_at ? new Date(r.auto_approve_at).toLocaleTimeString() : '—'}.
                          You cannot stop this once the cool-off elapses.
                        </p>
                      </div>
                      <Button
                        className="w-full bg-red-600 hover:bg-red-700 text-white"
                        onClick={() => handleBlock(r)}
                        disabled={acting}
                        data-testid={`recovery-key-approval-${r.id}-block-btn`}
                      >
                        <XCircle className="w-4 h-4 mr-1.5" />
                        Restrict & Force Logout
                      </Button>
                    </>
                  ) : (
                    <>
                      <div className="rounded-md bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-800 p-3 text-xs text-amber-900 dark:text-amber-200">
                        <p className="font-semibold mb-1">Before approving:</p>
                        <ul className="list-disc list-inside space-y-0.5">
                          <li>Confirm this person is who they say they are (call, voice match).</li>
                          <li>Confirm they actually need to rotate their key (lost authenticator, etc.).</li>
                          <li>If anything seems off, decline and reach out to security.</li>
                        </ul>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          variant="outline"
                          className="border-red-300 text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-300"
                          onClick={() => { setRejectTarget(r); setRejectReason(''); }}
                          disabled={acting}
                          data-testid={`recovery-key-approval-${r.id}-reject-btn`}
                        >
                          <XCircle className="w-4 h-4 mr-1.5" />
                          Decline
                        </Button>
                        <Button
                          className="bg-[#586D52] hover:bg-[#465A41]"
                          onClick={() => handleApprove(r)}
                          disabled={acting}
                          data-testid={`recovery-key-approval-${r.id}-approve-btn`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1.5" />
                          Approve
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!rejectTarget} onOpenChange={(v) => { if (!v) setRejectTarget(null); }}>
        <DialogContent className="max-w-sm" data-testid="recovery-key-reject-dialog">
          <DialogHeader>
            <DialogTitle>Decline this request?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {rejectTarget?.user_full_name} will be notified that their request was declined.
            </p>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Reason (optional, shown to user)</label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value.slice(0, 500))}
                placeholder="e.g. Couldn't verify identity over the phone"
                rows={3}
                data-testid="recovery-key-reject-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectTarget(null)} disabled={acting} data-testid="recovery-key-reject-cancel-btn">Cancel</Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={submitReject}
              disabled={acting}
              data-testid="recovery-key-reject-confirm-btn"
            >
              {acting ? 'Declining…' : 'Decline Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecoveryKeyApprovalsPage;
